import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
  useMemo,
  useEffect
} from 'react';
import { AvailableNewCitation, Campaign, CampaignDetails } from '../types';

// localStorage keys
const CITATION_BUILDER_STORAGE_KEY = 'citation_builder_state';
const getStorageKey = () => `${CITATION_BUILDER_STORAGE_KEY}`;

// Define a generic type for citation items (can be extended with specific types later)
export type CitationItem = Record<string, unknown>;

export interface SelectedBusinessProfile {
  accountId: string;
  name: string;
  locationId: string;
}

export interface CitationState {
  campaignId: number | string | null;
  BLLocationId: number | string | null;
  syncAndPost: boolean;
  campaignList?: Campaign[];
  campaignDetails?: CampaignDetails;
  selectedExistingCitations: string[];
  selectedAvailableNewCitations: AvailableNewCitation[];
  selectedBusinessProfile: SelectedBusinessProfile | null;
  error: {
    status: string;
    message: string;
  }
}

export enum CitationActionTypes {
  SET_CAMPAIGN_ID = 'SET_CAMPAIGN_ID',
  SET_BL_LOCATION_ID = 'SET_BL_LOCATION_ID',
  SET_SYNC_AND_POST = 'SET_SYNC_AND_POST',
  SET_CAMPAIGN_LIST = 'SET_CAMPAIGN_LIST',
  SET_CAMPAIGN_DETAILS = 'SET_CAMPAIGN_DETAILS',
  SET_SELECTED_EXISTING_CITATIONS = 'SET_SELECTED_EXISTING_CITATIONS',
  SET_SELECTED_AVAILABLE_NEW_CITATIONS = 'SET_SELECTED_AVAILABLE_NEW_CITATIONS',
  SET_SELECTED_BUSINESS_PROFILE = 'SET_SELECTED_BUSINESS_PROFILE',
  RESET_STATE = 'RESET_STATE',
  SET_ERROR = 'SET_ERROR'
}

type CitationAction =
  | {
      type: CitationActionTypes.SET_CAMPAIGN_ID;
      payload: number | string | null;
    }
  | {
      type: CitationActionTypes.SET_BL_LOCATION_ID;
      payload: number | string | null;
    }
  | { type: CitationActionTypes.SET_SYNC_AND_POST; payload: boolean }
  | { type: CitationActionTypes.SET_CAMPAIGN_LIST; payload: Campaign[] }
  | { type: CitationActionTypes.SET_CAMPAIGN_DETAILS; payload: CampaignDetails }
  | {
      type: CitationActionTypes.SET_SELECTED_EXISTING_CITATIONS;
      payload: string[];
    }
  | {
      type: CitationActionTypes.SET_SELECTED_AVAILABLE_NEW_CITATIONS;
      payload: AvailableNewCitation[];
    }
  | {
      type: CitationActionTypes.SET_SELECTED_BUSINESS_PROFILE;
      payload: SelectedBusinessProfile | null;
    }
  | { type: CitationActionTypes.RESET_STATE }
  | { type: CitationActionTypes.SET_ERROR; payload: { status: string; message: string } };

const initialState: CitationState = {
  campaignId: null,
  BLLocationId: null,
  syncAndPost: false,
  campaignList: [],
  campaignDetails: undefined,
  selectedExistingCitations: [],
  selectedAvailableNewCitations: [],
  selectedBusinessProfile: null,
  error: {
    status: '',
    message: ''
  }

};

const citationReducer = (
  state: CitationState,
  action: CitationAction
): CitationState => {
  switch (action.type) {
    case CitationActionTypes.SET_CAMPAIGN_ID:
      return {
        ...state,
        campaignId: action.payload
      };
    case CitationActionTypes.SET_BL_LOCATION_ID:
      return {
        ...state,
        BLLocationId: action.payload
      };
    case CitationActionTypes.SET_SYNC_AND_POST:
      return {
        ...state,
        syncAndPost: action.payload
      };
    case CitationActionTypes.SET_CAMPAIGN_LIST:
      return {
        ...state,
        campaignList: action.payload
      };
    case CitationActionTypes.SET_CAMPAIGN_DETAILS:
      return {
        ...state,
        campaignDetails: action.payload
      };
    case CitationActionTypes.SET_SELECTED_EXISTING_CITATIONS:
      return {
        ...state,
        selectedExistingCitations: action.payload
      };
    case CitationActionTypes.SET_SELECTED_AVAILABLE_NEW_CITATIONS:
      return {
        ...state,
        selectedAvailableNewCitations: action.payload
      };
    case CitationActionTypes.SET_SELECTED_BUSINESS_PROFILE:
      return {
        ...state,
        selectedBusinessProfile: action.payload
      };
    case CitationActionTypes.RESET_STATE:
      return {
        ...initialState,
        campaignList: state.campaignList
      };
    case CitationActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload
      };
    default:
      return state;
  }
};

interface CitationContextValue {
  state: CitationState;
  setCampaignId: (campaignId: number | string | null) => void;
  setBLLocationId: (BLLocationId: number | string | null) => void;
  setSyncAndPost: (citations: boolean) => void;
  setCampaignList: (campaigns: Campaign[]) => void;
  setCampaignDetails: (campaignDetails: CampaignDetails) => void;
  setSelectedExistingCitations: (selected: string[]) => void;
  setSelectedAvailableNewCitations: (selected: AvailableNewCitation[]) => void;
  setSelectedBusinessProfile: (profile: SelectedBusinessProfile | null) => void;
  resetState: () => void;
  restoreStateFromStorage: () => void;
  persistStateToStorage: () => void;
  clearStateFromStorage: () => void;
  getCampaignId: () => string | number | null;
  getBLLocationId: () => string | number | null;
  setError: (error: { status: string; message: string }) => void;
}

export const CitationContext = createContext<CitationContextValue | undefined>(
  undefined
);

interface CitationProviderProps {
  children: ReactNode;
}

// Helper functions for localStorage
const saveToStorage = (data: Partial<CitationState>) => {
  try {
    const key = getStorageKey();
    const serialized = JSON.stringify(data);
    localStorage.setItem(key, serialized);
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
};

const loadFromStorage = (): Partial<CitationState> | null => {
  try {
    const key = getStorageKey();
    const item = localStorage.getItem(key);
    if (item) {
      return JSON.parse(item);
    }
  } catch (error) {
    console.error('Failed to load from localStorage:', error);
  }
  return null;
};

export const CitationProvider = ({ children }: CitationProviderProps) => {
  const [state, dispatch] = useReducer(citationReducer, initialState);

  const setCampaignId = useCallback((campaignId: number | string | null) => {
    dispatch({
      type: CitationActionTypes.SET_CAMPAIGN_ID,
      payload: campaignId
    });
  }, []);
  const setSyncAndPost = useCallback((isSyncAndPost: boolean) => {
    dispatch({
      type: CitationActionTypes.SET_SYNC_AND_POST,
      payload: isSyncAndPost
    });
  }, []);

  const setCampaignList = useCallback((campaigns: Campaign[]) => {
    dispatch({
      type: CitationActionTypes.SET_CAMPAIGN_LIST,
      payload: campaigns
    });
  }, []);

  const setCampaignDetails = useCallback((campaignDetails: CampaignDetails) => {
    dispatch({
      type: CitationActionTypes.SET_CAMPAIGN_DETAILS,
      payload: campaignDetails
    });
  }, []);

  const setSelectedExistingCitations = useCallback((selected: string[]) => {
    dispatch({
      type: CitationActionTypes.SET_SELECTED_EXISTING_CITATIONS,
      payload: selected
    });
  }, []);

  const setSelectedAvailableNewCitations = useCallback(
    (selected: AvailableNewCitation[]) => {
      dispatch({
        type: CitationActionTypes.SET_SELECTED_AVAILABLE_NEW_CITATIONS,
        payload: selected
      });
    },
    []
  );

  const setSelectedBusinessProfile = useCallback(
    (profile: SelectedBusinessProfile | null) => {
      dispatch({
        type: CitationActionTypes.SET_SELECTED_BUSINESS_PROFILE,
        payload: profile
      });
    },
    []
  );

  const setBLLocationId = useCallback(
    (BLLocationId: number | string | null) => {
      dispatch({
        type: CitationActionTypes.SET_BL_LOCATION_ID,
        payload: BLLocationId
      });
    },
    []
  );

  const resetState = useCallback(() => {
    dispatch({ type: CitationActionTypes.RESET_STATE });
  }, []);

  const setError = useCallback((error: { status: string; message: string }) => {
    dispatch({
      type: CitationActionTypes.SET_ERROR,
      payload: error
    });
  }, []);

  // Persist user selections to localStorage
  const persistStateToStorage = useCallback(
    () => {
      // if (!campaignId) return;

      let dataToPersist = {};

      if (state.selectedExistingCitations.length > 0) {
        dataToPersist = {
          ...dataToPersist,
          selectedExistingCitations: state.selectedExistingCitations
        };
      }
      if (state.selectedAvailableNewCitations.length > 0) {
        dataToPersist = {
          ...dataToPersist,
          selectedAvailableNewCitations: state.selectedAvailableNewCitations
        };
      }
      if (state.syncAndPost) {
        dataToPersist = {
          ...dataToPersist,
          syncAndPost: state.syncAndPost
        };
      }
      if (state.selectedBusinessProfile) {
        dataToPersist = {
          ...dataToPersist,
          selectedBusinessProfile: state.selectedBusinessProfile
        };
      }
      if (state.campaignDetails) {
        dataToPersist = {
          ...dataToPersist,
          campaignDetails: state.campaignDetails
        };
      }
      // if (state.BLLocationId) {
      // set BLLocationId
      // };
      dataToPersist = {
        ...dataToPersist,
        BLLocationId: state.BLLocationId
      }

      saveToStorage(dataToPersist);
    },
    [
      state.selectedExistingCitations,
      state.selectedAvailableNewCitations,
      state.syncAndPost,
      state.campaignDetails,
      state.selectedBusinessProfile,
      state.BLLocationId
    ]
  );

  // Restore user selections from localStorage
  const restoreStateFromStorage = useCallback(() => {
    // if (!campaignId) return;

    const savedData = loadFromStorage();
    if (savedData) {
      if (savedData.selectedExistingCitations) {
        dispatch({
          type: CitationActionTypes.SET_SELECTED_EXISTING_CITATIONS,
          payload: savedData.selectedExistingCitations
        });
      }
      if (savedData.selectedAvailableNewCitations) {
        dispatch({
          type: CitationActionTypes.SET_SELECTED_AVAILABLE_NEW_CITATIONS,
          payload: savedData.selectedAvailableNewCitations
        });
      }
      if (savedData.syncAndPost !== undefined) {
        dispatch({
          type: CitationActionTypes.SET_SYNC_AND_POST,
          payload: savedData.syncAndPost
        });
      }
      if (savedData.selectedBusinessProfile !== undefined) {
        dispatch({
          type: CitationActionTypes.SET_SELECTED_BUSINESS_PROFILE,
          payload: savedData.selectedBusinessProfile
        });
      }
      if (savedData.BLLocationId !== undefined) {
        dispatch({
          type: CitationActionTypes.SET_BL_LOCATION_ID,
          payload: savedData.BLLocationId
        });
      }
    }
  }, []);

  const clearStateFromStorage = useCallback(() => {
    try {
      const key = getStorageKey();
      localStorage.removeItem(key);
      dispatch({
        type: CitationActionTypes.RESET_STATE
      });
    } catch (error) {
      console.error('Failed to clear state from localStorage:', error);
    }
  }, []);

  const getCampaignId = useCallback(() => {
    return state.campaignId || state.campaignDetails?.campaign_id || null;
  }, [state.campaignDetails?.campaign_id, state.campaignId]);

  const getBLLocationId = useCallback(() => {
    return state.BLLocationId || null;
  }, [state.BLLocationId]);

  // Restore user selections from localStorage on mount
  useEffect(() => {
    restoreStateFromStorage();
}, []);

  // Persist state to localStorage whenever user selections change
  useEffect(() => {
      persistStateToStorage();
  }, [
    state.selectedExistingCitations,
    state.selectedAvailableNewCitations,
    state.syncAndPost,
    state.selectedBusinessProfile,
    state.campaignDetails?.campaign_id,
    state.BLLocationId,
    persistStateToStorage
  ]);

  // Memoize the context value to prevent unnecessary re-renders
  // Only recreate when state or any of the setter functions change
  // Since all setters are memoized with useCallback, they're stable
  const value: CitationContextValue = useMemo(
    () => ({
      state,
      setCampaignId,
      setBLLocationId,
      setSyncAndPost,
      setCampaignList,
      setCampaignDetails,
      setSelectedExistingCitations,
      setSelectedAvailableNewCitations,
      setSelectedBusinessProfile,
      resetState,
      restoreStateFromStorage,
      persistStateToStorage,
      clearStateFromStorage,
      getCampaignId,
      getBLLocationId,
      setError
    }),
    [
      state,
      setCampaignId,
      setBLLocationId,
      setSyncAndPost,
      setCampaignList,
      setCampaignDetails,
      setSelectedExistingCitations,
      setSelectedAvailableNewCitations,
      setSelectedBusinessProfile,
      resetState,
      restoreStateFromStorage,
      persistStateToStorage,
      clearStateFromStorage,
      getCampaignId,
      getBLLocationId,
      setError
    ]
  );

  return (
    <CitationContext.Provider value={value}>
      {children}
    </CitationContext.Provider>
  );
};

export const useCitation = (): CitationContextValue => {
  const context = useContext(CitationContext);
  if (context === undefined) {
    throw new Error('useCitation must be used within a CitationProvider');
  }
  return context;
};

export const useClearCitationStateOnMount = () => {
  const { clearStateFromStorage } = useCitation();
  useEffect(() => {
    clearStateFromStorage();
  }, []);
};